<template>
  <div>
    <!-- <ClientOnly> -->
  <NavSimpleView
    v-if="config?.styleType === 'simple' && !isErpBuySite"
    :config="config"
    :theme-vars="themeVars"
    :isErpBuySite="isErpBuySite"
    :showNavigationList="showNavigationList"
    :actionIndex="actionIndex"
    :mallUserInfo="mallUserInfo"
    :logout="logout"
    :getLocaleText="getLocaleText"
    :getJumpType="getJumpType"
  />
  <NavDefaultView
    v-else
    :config="config"
    :theme-vars="themeVars"
    :isErpBuySite="isErpBuySite"
    v-model:searchKeyword="searchInputVal"
    v-model:searchType="searchTypeVal"
    :mallUserInfo="mallUserInfo"
    :showNavigationList="showNavigationList"
    :actionIndex="actionIndex"
    :getLocaleText="getLocaleText"
    :getJumpType="getJumpType"
    :onKeywordSearch="onKeywordSearch"
    :openImgSearch="openImgSearch"
    :logout="logout"
  />
  <!-- </ClientOnly> -->
  </div>
</template>

<script setup>
import { ref, onMounted,onBeforeMount, computed, watch } from "vue";
import { getTabBarConfigList } from "@/api/index.js";
import { extract1688OfferId, extractTaobaoItemId, extractWeidianItemId } from "@/utils/index.js";
import useLocate from "~/components/render/uses/useLocate.js";
import { useMainStore } from "~/composables/store";
import { useNuxtApp } from "#app";
import { legacyPathToUserCenterPath } from "~/user-center/routes";
const { $isErpBuySite } = useNuxtApp()
import { useRoute } from "vue-router";
import { get } from "lodash-es";
import NavDefaultView from "./NavDefaultView.vue";
import NavSimpleView from "./NavSimpleView.vue";
const store = useMainStore();
const isErpBuySite = computed(() => Boolean($isErpBuySite?.value));

// 引入自定义 mixins (合并到 composable 中)
const { getLocaleText } = useLocate();

function logout() {
  store.logout();
}
const props = defineProps({
 
  parentKeys: {
    type: Array,
    default: () => []
  },
  disabled: {
    type: Boolean,
    default: true
  },
  config: {
    type: Object,
    default: () => ({})
  }
});

const actionIndex = ref(0);
const activeIndex = ref(null);
const navigationList = ref([]);
const searchInputVal = ref("");
const searchTypeVal = ref("1");
const edit = computed(() => !props.disabled);
const mallUserInfo = computed(() => get(store, "$state.mallUserInfo") || {});

navigationList.value = Array.isArray(get(store, "$state.navigationList"))
  ? get(store, "$state.navigationList")
  : [];

const showNavigationList = computed(() =>{
  if(props.config && props.config.navigationList && props.config.navigationList.length){
    return props.config.navigationList;
  }
  const storeNavigationList = get(store, "$state.navigationList") || [];
  if (Array.isArray(storeNavigationList) && storeNavigationList.length) {
    return storeNavigationList;
  }
  return navigationList.value || [];
});

const isSimple = computed(() => props.config?.styleType === "simple" && !isErpBuySite.value);
const isDarkMode = computed(() => props.config?.themeMode === "dark");
const navBackgroundColor = computed(() => {
  const backgroundColor = String(props.config?.backgroundColor || "").trim();
  if (backgroundColor) {
    return backgroundColor;
  }
  return isDarkMode.value ? "#111827" : "#ffffff";
});
const themeVars = computed(() => {
  if (isDarkMode.value) {
    return {
      mode: "dark",
      navBackgroundColor: navBackgroundColor.value,
      navTextColor: props.config?.textConfigs?.fontColor || "#f3f4f6",
      navActiveTextColor: props.config?.textConfigs?.fontActionColor || "#93c5fd",
      navSubtleTextColor: "#d1d5db",
      navMutedTextColor: "#9ca3af",
      navBorderColor: "rgba(255, 255, 255, 0.14)",
      navDividerColor: "rgba(255, 255, 255, 0.16)",
      navPanelBackground: "rgba(255, 255, 255, 0.08)",
      navPanelHoverBackground: "rgba(255, 255, 255, 0.12)",
      navInputBackground: "rgba(17, 24, 39, 0.72)",
      navInputTextColor: "#f9fafb",
      navInputPlaceholderColor: "#d1d5db",
      navShadowColor: "rgba(0, 0, 0, 0.38)",
      navCategoryBackground: "#b4272b",
      navSearchAccent: "#b4272b",
      navPrimaryButtonBackground: "#ff7228",
      navPrimaryButtonTextColor: "#ffffff",
      navGhostButtonBackground: "rgba(255, 255, 255, 0.04)",
      navGhostButtonBorderColor: "rgba(255, 255, 255, 0.22)",
      navGhostButtonTextColor: "#f9fafb",
      navControlTextColor: "#f3f4f6",
      navControlSubtleTextColor: "#d1d5db",
      navControlIconColor: "#e5e7eb",
    };
  }
  return {
    mode: "light",
    navBackgroundColor: navBackgroundColor.value,
    navTextColor: props.config?.textConfigs?.fontColor || "#333333",
    navActiveTextColor: props.config?.textConfigs?.fontActionColor || "#346bd2",
    navSubtleTextColor: "#44403c",
    navMutedTextColor: "#78716c",
    navBorderColor: "#d6d3d1",
    navDividerColor: "#dfdfdf",
    navPanelBackground: "#f6f6f6",
    navPanelHoverBackground: "#f5f5f4",
    navInputBackground: "#ffffff",
    navInputTextColor: "#000000",
    navInputPlaceholderColor: "#999999",
    navShadowColor: "rgba(26, 24, 23, 0.12)",
    navCategoryBackground: "#b4272b",
    navSearchAccent: "#b4272b",
    navPrimaryButtonBackground: "#ff7228",
    navPrimaryButtonTextColor: "#ffffff",
    navGhostButtonBackground: "#ffffff",
    navGhostButtonBorderColor: "#1a1817",
    navGhostButtonTextColor: "#1a1817",
    navControlTextColor: "#44403c",
    navControlSubtleTextColor: "#57534e",
    navControlIconColor: "#78716c",
  };
});

const route = useRoute();

function normalizeComparablePath(rawPath = "") {
  const input = String(rawPath || "").trim();
  if (!input) {
    return "";
  }
  if (/^(https?:)?\/\//.test(input)) {
    return input;
  }
  const normalizedInput = legacyPathToUserCenterPath(input);
  try {
    const url = new URL(normalizedInput, "http://localhost");
    const pathname = url.pathname !== "/" ? url.pathname.replace(/\/+$/, "") : "/";
    return `${pathname}${url.search}${url.hash}`;
  } catch {
    return normalizedInput;
  }
}

function getMatchScore(currentFullPath, linkUrl) {
  if (!linkUrl || /^(https?:)?\/\//.test(linkUrl)) {
    return -1;
  }

  const normalizedCurrent = normalizeComparablePath(currentFullPath || "/");
  const normalizedLink = normalizeComparablePath(linkUrl);

  if (!normalizedCurrent || !normalizedLink) {
    return -1;
  }

  if (normalizedLink === "/") {
    return normalizedCurrent === "/" ? 100 : -1;
  }

  if (normalizedCurrent === normalizedLink) {
    return 1000 + normalizedLink.length;
  }

  if (normalizedLink.startsWith("/v?url")) {
    return -1;
  }

  const [currentPathname] = normalizedCurrent.split(/[?#]/);
  const [linkPathname] = normalizedLink.split(/[?#]/);

  if (linkPathname && currentPathname === linkPathname) {
    return 800 + linkPathname.length;
  }

  if (linkPathname && currentPathname.startsWith(`${linkPathname}/`)) {
    return 600 + linkPathname.length;
  }

  if (linkPathname && normalizedCurrent.startsWith(`${linkPathname}?`)) {
    return 500 + linkPathname.length;
  }

  return -1;
}

function syncActionIndexByRoute() {
  const currentFullPath = route.fullPath || "/";
  const list = Array.isArray(showNavigationList.value) ? showNavigationList.value : [];
  let bestIndex = -1;
  let bestScore = -1;

  list.forEach((item, index) => {
    const score = getMatchScore(currentFullPath, item?.linkUrl);
    if (score > bestScore) {
      bestScore = score;
      bestIndex = index;
    }
  });

  actionIndex.value = bestIndex;
}

function syncSearchTypeByRoute() {
  const platform = String(route.query?.platform || "").trim();
  if (platform === "taobao" || String(route.path || "").startsWith("/tb")) {
    searchTypeVal.value = "2";
    return;
  }
  searchTypeVal.value = "1";
}

const getPcNavigatorData = async () => {
  const param = {
    pageNo: 0,
    pageSize: 9999,
    showType: "1",
    status: "1"
  };

  if ((!Array.isArray(navigationList.value) || !navigationList.value.length) && process.client) {
    try{
      const res = await getTabBarConfigList(param);
      if (res.data && res.data.dataList) {
        const list = res.data.dataList.sort((a, b) => {
          const aSort = a.sortNo ?? 0;
          const bSort = b.sortNo ?? 0;
          return aSort - bSort;
        });
        navigationList.value = list;
      }
    }catch(e){
      console.log("getTabBarConfigList",e)
    }
  }
  syncActionIndexByRoute();
};

function openImgSearch() {
  navigateTo({
    path: "/product/search",
    query: {
      searchType: "img",
      imgSrc: "",
      platform: searchTypeVal.value === "2" ? "taobao" : "1688",
      key: Date.now(),
    }
  });
}

function onKeywordSearch() {
  const keyword = String(searchInputVal.value || "").trim();
  if (!keyword) return;

  const offerId = extract1688OfferId(keyword);
  if (offerId) {
    navigateTo({
      path: "/product",
      query: {
        detailId: offerId,
        key: Date.now(),
      }
    });
    return;
  }

  const taobaoItemId = extractTaobaoItemId(keyword);
  if (taobaoItemId) {
    navigateTo(`/tb/goods/${encodeURIComponent(taobaoItemId)}`);
    return;
  }

  const weidianItemId = extractWeidianItemId(keyword);
  if (weidianItemId) {
    navigateTo(`/wd/goods/${encodeURIComponent(weidianItemId)}`);
    return;
  }

  navigateTo({
    path: "/product/search",
    query: {
      keyword,
      platform: searchTypeVal.value === "2" ? "taobao" : "1688",
      key: Date.now(),
    }
  });
}

syncSearchTypeByRoute();
syncActionIndexByRoute();

watch(
  () => showNavigationList.value,
  async value => {
    if (value) {
      syncActionIndexByRoute();
    }
  },
  { deep: true, immediate: true }
);
watch(
  () => route.fullPath,
  async value => {
    if (value) {
      syncSearchTypeByRoute();
      syncActionIndexByRoute();
    }
  },
  { immediate: true }
);

onMounted(() => {
  if (typeof window !== "undefined") {
    if (!window.pageTabs) {
      window.pageTabs = {};
    }
    window.pageTabs[props.config.id] = {
      setTab,
      getTab
    };
  }

  getPcNavigatorData();
});

onBeforeMount(()=>{
  syncSearchTypeByRoute();
  if(process.client){
    store.getCurrencyData();
  }
  
});

const getTab = () => activeIndex.value;
const setTab = i => {
  if (props.config.navigationItemConfigs[i]) {
    activeIndex.value = props.config.navigationItemConfigs[i].config.text + i;
  }
};
const getJumpType = (item, isNuxtLink) => {
  let jumpWay = "_Self";
  switch (item.linkType) {
    case 0:
      jumpWay = "_Blank";
      break;
    case 10:
      jumpWay = "_Self";
      break;
  }
  if (isNuxtLink && jumpWay == "_Self") {
    return undefined;
  }
  return jumpWay;
};

const navigationClickFn = (item, index) => {
  if (!item) return;
  actionIndex.value = index;
  let jumpWay = getJumpType(item);

  if (item.linkUrl) {
    window.open(item.linkUrl, jumpWay);
  }
};

const handleSelect = value => {
  // handle select logic
};
</script>
